import { useGetTransactions } from "@/hooks/useGetTransactions";
import { useMe } from "@/hooks/useMe";
import { useUploadUnsycedTransactions } from "@/hooks/useUploadUnsycedTransactions";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, useLocation } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { Toaster } from "react-hot-toast";
import { lazy, useEffect, useState } from "react";
import { OverviewFooter } from "@/components/Overview/OverviewFooter";
import { hasExpensesAtom } from "@/store/expense";
import { useAtomValue } from "jotai";
import { useMounted } from "@mantine/hooks";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { expensesAtom } from "@/store/global";

const DevTools =
  import.meta.env.DEV || import.meta.env.USE_DEV_TOOLS || process.env.USE_DEV_TOOLS
    ? lazy(() => import("@/development/DevUtils").then((module) => ({ default: module.DevTools })))
    : null;

const isBot = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const botPatterns = [
    'googlebot', 'bingbot', 'yandexbot', 'duckduckbot', 'slurp',
    'crawler', 'spider', 'bot', 'crawl'
  ];
  return botPatterns.some(pattern => userAgent.includes(pattern));
};

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: () => {
    const { pathname } = useLocation();
    const mounted = useMounted();

    const hasExpenses = useAtomValue(hasExpensesAtom);

    useGetTransactions();
    useUploadUnsycedTransactions();
    useMe();

    const hiddenFooterPaths = ["/pengeluaran"];
    const showFooter = !hiddenFooterPaths.includes(pathname) && hasExpenses;

    const [showUpgradeAlert, setShowUpgradeAlert] = useState(false);

    useEffect(() => {
      if (!hasExpenses && mounted && !isBot()) {
        const timer = setTimeout(() => {
          setShowUpgradeAlert(true);
        }, 2000);

        return () => clearTimeout(timer);
      }
    }, [hasExpenses, mounted]);

    return (
      <>
        <Toaster
          toastOptions={{
            style: { fontFamily: "Satoshi, sans-serif" },
          }}
        />

        {showFooter && <OverviewFooter />}
        <Outlet />

        <AlertDialog open={showUpgradeAlert} onOpenChange={setShowUpgradeAlert}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                <a 
                  href="https://web.natauang.com"
                  className="hover:underline cursor-pointer"
                >
                  Versi Baru Tersedia di web.natauang.com
                </a>
              </AlertDialogTitle>
              <AlertDialogDescription>
                Kamu menggunakan versi lama Natauang. Apakah kamu ingin menggunakan versi baru?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Tidak</AlertDialogCancel>
              <AlertDialogAction onClick={() => {
                window.location.href = "https://web.natauang.com";
              }}>
                Ya
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {DevTools && <DevTools />}
        {import.meta.env.DEV && <TanStackRouterDevtools />}
      </>
    );
  },
});
